const MOBILE_QUERY = '(max-width: 767px)';
const FLIP_DURATION = 650;

class CatalogBook extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-catalog-viewport]');
    this.spread = this.querySelector('[data-catalog-spread]');
    this.pages = Array.from(this.querySelectorAll('[data-catalog-page]'));
    this.prevButton = this.querySelector('[data-catalog-prev]');
    this.nextButton = this.querySelector('[data-catalog-next]');
    this.status = this.querySelector('[data-catalog-status]');

    if (this.pages.length === 0) return;

    this.singleOnMobile = this.hasAttribute('data-single-on-mobile');
    this.mobileQuery = window.matchMedia(MOBILE_QUERY);
    this.isAnimating = false;

    // `index` points at the current entry in the spread model (see buildSpreads).
    this.index = 0;
    // Suppresses the live-region announcement on the initial render so the
    // page doesn't read "Page 1 of N" the moment it loads.
    this.hasRendered = false;
    this.buildSpreads();

    // In the theme editor, every settings change re-renders the section and
    // recreates this element, which would otherwise snap back to the cover.
    // Restore the last viewed spread so editing a hot spot keeps your place.
    this.restoreIndex();

    this.onPrev = () => this.flip('prev');
    this.onNext = () => this.flip('next');
    this.onPageClick = this.handlePageClick.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onViewportChange = () => {
      this.buildSpreads();
      this.render();
    };

    this.prevButton?.addEventListener('click', this.onPrev);
    this.nextButton?.addEventListener('click', this.onNext);
    this.viewport?.addEventListener('click', this.onPageClick);
    this.addEventListener('keydown', this.onKeydown);
    this.mobileQuery.addEventListener('change', this.onViewportChange);

    this.setAttribute('tabindex', '0');
    this.render();

    // Enable transitions only after the initial layout is painted so the first
    // cover positioning doesn't animate in from a flat spread.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.classList.add('catalog-book--ready'));
    });
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener('click', this.onPrev);
    this.nextButton?.removeEventListener('click', this.onNext);
    this.viewport?.removeEventListener('click', this.onPageClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.mobileQuery?.removeEventListener('change', this.onViewportChange);
  }

  get isSinglePage() {
    return this.singleOnMobile && this.mobileQuery.matches;
  }

  /**
   * Build the ordered list of "spreads" the reader pages through. Each spread is
   * an array of one or two page indices.
   *
   * Desktop (magazine): the book starts closed on the front cover (a single
   * page), opens to two-page spreads through the interior, and ends on the back
   * cover as a single page whenever it falls unpaired:
   *   [0] [1,2] [3,4] ... [last]
   *
   * Mobile single-page mode: one page per spread.
   */
  buildSpreads() {
    const count = this.pages.length;
    const spreads = [];

    if (this.isSinglePage) {
      for (let i = 0; i < count; i += 1) spreads.push([i]);
    } else {
      // Front cover alone.
      spreads.push([0]);
      // Interior pages pair up starting at index 1.
      let i = 1;
      while (i < count) {
        if (i + 1 < count) {
          spreads.push([i, i + 1]);
          i += 2;
        } else {
          // Lone trailing page = back cover, shown closed.
          spreads.push([i]);
          i += 1;
        }
      }
    }

    this.spreads = spreads;
    if (this.index > spreads.length - 1) this.index = spreads.length - 1;
    if (this.index < 0) this.index = 0;
  }

  currentSpread() {
    return this.spreads[this.index] || [];
  }

  /** Only persist position inside the theme editor, not on the storefront. */
  get persists() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  get storageKey() {
    return `catalog-book:${this.getAttribute('data-section-id') || 'default'}`;
  }

  restoreIndex() {
    if (!this.persists) return;
    try {
      const saved = sessionStorage.getItem(this.storageKey);
      if (saved === null) return;
      const index = parseInt(saved, 10);
      if (!Number.isNaN(index)) {
        this.index = Math.max(0, Math.min(index, this.spreads.length - 1));
      }
    } catch (e) {
      // sessionStorage may be unavailable (private mode); fall back to cover.
    }
  }

  saveIndex() {
    if (!this.persists) return;
    try {
      sessionStorage.setItem(this.storageKey, String(this.index));
    } catch (e) {
      // Ignore storage failures.
    }
  }

  handlePageClick(event) {
    if (this.isAnimating) return;
    // Let hot spot links open the product instead of turning the page.
    if (event.target.closest('[data-catalog-hotspot]')) return;
    const page = event.target.closest('[data-catalog-page]');
    if (!page || !this.viewport.contains(page)) return;

    const rect = this.viewport.getBoundingClientRect();
    const isLeftHalf = event.clientX - rect.left < rect.width / 2;
    this.flip(isLeftHalf ? 'prev' : 'next');
  }

  handleKeydown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.flip('next');
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.flip('prev');
    }
  }

  canFlip(direction) {
    if (this.isAnimating) return false;
    if (direction === 'next') return this.index < this.spreads.length - 1;
    return this.index > 0;
  }

  flip(direction) {
    if (!this.canFlip(direction)) return;
    this.isAnimating = true;

    const fromIndex = this.index;
    const fromSpread = this.currentSpread();
    const toIndex = direction === 'next' ? this.index + 1 : this.index - 1;
    const toSpread = this.spreads[toIndex];

    const leaf = this.buildLeaf(direction, fromSpread, toSpread);

    if (!leaf) {
      this.index = toIndex;
      this.isAnimating = false;
      this.render();
      this.saveIndex();
      return;
    }

    // The leaf's two faces (front = outgoing page, back = incoming page) carry
    // those pages during the flip. To avoid the incoming page also showing flat
    // underneath — which looks like the page appears, then flips onto itself —
    // the underlay must show the *other* pages only:
    //   next on a spread: keep old-left, reveal new-right; leaf does old→new mid.
    //   prev on a spread: keep old-right, reveal new-left.
    // Single-page covers have no "other" page, so the underlay is just empty.
    const underlay = this.transitionalSides(direction, fromSpread, toSpread);

    // 1. Lay out the SOURCE state at its starting translate and mount the leaf
    //    flat over the page it's about to peel.
    this.index = fromIndex;
    this.render({ keepLeaf: leaf });
    this.spread.appendChild(leaf);

    // 2. Commit that starting frame.
    void leaf.offsetWidth;

    // 3. Advance to the destination layout (so the spread slides) but keep the
    //    underlay pages — the leaf peels in lockstep, revealing them cleanly.
    this.index = toIndex;
    this.render({ keepLeaf: leaf, override: underlay });
    leaf.classList.add('catalog-leaf--flipped');

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      leaf.remove();
      this.isAnimating = false;
      // Settle the clean destination layout (drops the source page + reveal
      // bookkeeping). The translate is already in place, so nothing jumps.
      this.render();
      this.saveIndex();
    };

    leaf.addEventListener('transitionend', (event) => {
      if (event.propertyName === 'transform') finish();
    });
    // Fallback in case transitionend doesn't fire.
    setTimeout(finish, FLIP_DURATION + 120);
  }

  /**
   * The pages to show flat beneath the leaf during a flip, keyed by page index
   * → 'left'|'right'. It deliberately excludes the two pages carried by the leaf
   * faces (outgoing + incoming), so the incoming page is revealed only by the
   * peel, never shown flat first.
   */
  transitionalSides(direction, fromSpread, toSpread) {
    // Mobile: the full-width leaf's back face rotates off-screen, so the
    // incoming page must sit flat beneath the leaf to be revealed as the
    // current page (leaf front) sweeps away. Show the destination page.
    if (this.isSinglePage) {
      return { [toSpread[0]]: null };
    }

    const sides = {};
    if (direction === 'next') {
      // Keep the outgoing left page; reveal the incoming right page (if any).
      if (fromSpread.length === 2) sides[fromSpread[0]] = 'left';
      if (toSpread.length === 2) sides[toSpread[1]] = 'right';
    } else {
      // Keep the outgoing right page; reveal the incoming left page (if any).
      if (fromSpread.length === 2) sides[fromSpread[1]] = 'right';
      if (toSpread.length === 2) sides[toSpread[0]] = 'left';
    }
    return sides;
  }

  /**
   * Build a flipping leaf whose two faces show the outgoing page (front) and
   * the incoming page (back), pivoting on the book's spine. The face indices
   * are read from the spread model so single-page covers flip correctly.
   */
  buildLeaf(direction, fromSpread, toSpread) {
    const leaf = document.createElement('div');
    leaf.className = 'catalog-leaf';

    const fromSingle = fromSpread.length === 1;

    let frontIndex;
    let backIndex;

    if (direction === 'next') {
      // The rightmost visible page peels left to reveal the destination.
      leaf.classList.add('catalog-leaf--right');
      frontIndex = fromSingle ? fromSpread[0] : fromSpread[1];
      backIndex = toSpread[0];
    } else {
      // The leftmost visible page peels right to reveal the destination.
      leaf.classList.add('catalog-leaf--left');
      frontIndex = fromSpread[0];
      backIndex = toSpread[toSpread.length - 1];
    }

    // The leaf spans the full viewport only in mobile single-page mode. On
    // desktop, covers are half-width pages positioned by the spread's slide, so
    // every desktop leaf is a half-width page pivoting on the spine.
    if (this.isSinglePage) leaf.classList.add('catalog-leaf--single');

    const front = this.cloneFace(frontIndex, 'front');
    const back = this.cloneFace(backIndex, 'back');
    if (!front && !back) return null;
    leaf.append(front, back);
    return leaf;
  }

  cloneFace(pageIndex, faceName) {
    const face = document.createElement('div');
    face.className = `catalog-leaf__face catalog-leaf__face--${faceName}`;
    const source = this.pages[pageIndex];
    if (source) {
      const img = source.querySelector('img');
      if (img) {
        const clone = img.cloneNode(true);
        clone.removeAttribute('loading');
        face.appendChild(clone);
      } else {
        Array.from(source.childNodes).forEach((node) => {
          face.appendChild(node.cloneNode(true));
        });
        face.classList.add('catalog-leaf__face--placeholder');
      }
    } else {
      face.classList.add('catalog-leaf__face--blank');
    }
    return face;
  }

  /**
   * Which half each page of the current spread sits on, and the book state
   * class that positions the spread (slide for covers, centered for a spread).
   *   front cover  → lone page on the right half, spread slid left to center it
   *   back cover   → lone page on the left half, spread slid right to center it
   *   inner spread → [left, right]
   */
  layoutFor(index) {
    const spread = this.spreads[index] || [];
    const single = spread.length === 1;

    if (!single) {
      return {
        bookClass: '',
        sides: { [spread[0]]: 'left', [spread[1]]: 'right' },
      };
    }
    if (index === 0) {
      return { bookClass: 'catalog-book--cover-front', sides: { [spread[0]]: 'right' } };
    }
    return { bookClass: 'catalog-book--cover-back', sides: { [spread[0]]: 'left' } };
  }

  /**
   * Apply the book state class for the current index. This drives the spread's
   * translateX slide; kept separate from render() so a flip can commit the
   * source layout, then transition to the destination layout in the next frame.
   */
  applyLayout() {
    const mobile = this.isSinglePage;
    this.classList.toggle('catalog-book--mobile', mobile);

    const { bookClass } = mobile ? { bookClass: '' } : this.layoutFor(this.index);
    this.classList.toggle('catalog-book--cover-front', bookClass === 'catalog-book--cover-front');
    this.classList.toggle('catalog-book--cover-back', bookClass === 'catalog-book--cover-back');
  }

  /**
   * Show/hide the pages of the current spread and set each one's left/right
   * half. During a flip the animating leaf (kept via keepLeaf) overlays these
   * with cloned faces. `override` replaces the visible pages/sides with an
   * explicit map (used mid-flip so the incoming page isn't shown flat early).
   */
  render({ keepLeaf = null, override = null } = {}) {
    const mobile = this.isSinglePage;

    // `active` is the set of pages to display; `sides` maps page index → half.
    // Mobile pages are full-width and take no side class.
    let active;
    let sides;
    if (override) {
      active = new Set(Object.keys(override).map(Number));
      sides = override;
    } else if (mobile) {
      active = new Set(this.currentSpread());
      sides = {};
    } else {
      sides = this.layoutFor(this.index).sides;
      active = new Set(Object.keys(sides).map(Number));
    }

    // Pages in the current spread are exposed to assistive tech; all others are
    // hidden so a screen reader only reads what's visually on screen. This is
    // keyed off the canonical spread (real index), not the transient mid-flip
    // override, so the a11y tree always matches where the reader actually is.
    const exposed = new Set(this.currentSpread());

    this.pages.forEach((page, index) => {
      const side = sides[index];
      page.classList.toggle('catalog-page--active', active.has(index));
      page.classList.toggle('catalog-page--left', side === 'left');
      page.classList.toggle('catalog-page--right', side === 'right');
      page.toggleAttribute('aria-hidden', !exposed.has(index));
    });

    // Remove any stale leaves except the one currently animating.
    this.spread.querySelectorAll('.catalog-leaf').forEach((leaf) => {
      if (leaf !== keepLeaf) leaf.remove();
    });

    this.applyLayout();
    this.updateControls();

    // Announce the settled spread (not transient mid-flip override states), but
    // skip the very first render so nothing is read aloud on page load.
    if (!override) {
      if (this.hasRendered) this.announce();
      this.hasRendered = true;
    }
  }

  announce() {
    if (!this.status) return;
    const total = this.pages.length;
    const spread = this.currentSpread();
    const first = spread[0] + 1;
    const last = spread[spread.length - 1] + 1;
    this.status.textContent = first === last
      ? `Page ${first} of ${total}`
      : `Pages ${first} to ${last} of ${total}`;
  }

  updateControls() {
    if (this.prevButton) this.prevButton.disabled = this.index <= 0;
    if (this.nextButton) this.nextButton.disabled = this.index >= this.spreads.length - 1;
  }
}

if (!customElements.get('catalog-book')) {
  customElements.define('catalog-book', CatalogBook);
}
